import { MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const WHATSAPP_NUMBER = '4901797007958';

export function getWhatsAppLink(message = '') {
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encoded}` : ''}`;
}

export function getWhatsAppLinkToCustomer(customerPhone, message = '') {
  // Clean phone number - remove spaces, slashes, leading 0
  let phone = customerPhone.replace(/[\s\/\-\(\)]/g, '');
  if (phone.startsWith('0')) phone = '49' + phone.substring(1);
  if (!phone.startsWith('+') && !phone.startsWith('49')) phone = '49' + phone;
  phone = phone.replace('+', '');
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${phone}${message ? `?text=${encoded}` : ''}`;
}

export function generateOrderConfirmation(order) {
  const items = order.items?.map(i => `  ${i.quantity}x ${i.name}`).join('\n') || '';
  const type = order.fulfillment === 'delivery' ? 'Lieferung' : 'Abholung';
  return `Hallo ${order.customer_name},\n\nvielen Dank für Ihre Bestellung bei Ristorante Little Italy! 🍕\n\n📋 *Ihre Bestellung:*\n${items}\n\n💰 *Gesamt:* ${order.total?.toFixed(2)} €\n📦 *Art:* ${type}\n\n🌐 _Diese Bestellung wurde über die Webseite aufgegeben._\n\nWir bereiten Ihre Bestellung mit Liebe zu! ❤️\n\nRistorante Little Italy\nAlter Markt 6, 69412 Eberbach\n📞 0179 / 700 79 58`;
}

export function generateReservationConfirmation(reservation) {
  const name = reservation.name || `${reservation.first_name || ''} ${reservation.last_name || ''}`.trim();
  return `Hallo ${name},\n\nvielen Dank für Ihre Reservierung bei Ristorante Little Italy! 🍷\n\n📅 *Datum:* ${reservation.date}\n🕐 *Uhrzeit:* ${reservation.time}\n👥 *Personen:* ${reservation.party_size}\n\nWir freuen uns auf Ihren Besuch! ❤️\n\nRistorante Little Italy\nAlter Markt 6, 69412 Eberbach\n📞 0179 / 700 79 58`;
}

export function generateNewOrderNotification(order) {
  const items = order.items?.map(i => `${i.quantity}x ${i.name}`).join(', ') || '';
  const type = order.fulfillment === 'delivery' ? 'Lieferung' : 'Abholung';
  return `*Neue Bestellung!*\n\n${order.customer_name}\n${order.customer_phone}\n${type}\n\n${items}\n\n*Gesamt:* ${order.total?.toFixed(2)} EUR${order.notes ? `\nNotiz: ${order.notes}` : ''}`;
}

export function generateCustomerWhatsAppOrder(order) {
  const lines = [];
  lines.push('Hallo, ich möchte folgende Bestellung verbindlich bestätigen:');
  lines.push('');
  lines.push(`*Name:* ${order.customer_name}`);
  lines.push(`*Telefon:* ${order.customer_phone}`);
  if (order.customer_email) lines.push(`*E-Mail:* ${order.customer_email}`);
  lines.push('');
  lines.push('*Bestellung:*');
  if (order.items?.length) {
    order.items.forEach(i => {
      lines.push(`- ${i.quantity}x ${i.name} (${i.price?.toFixed(2)} EUR)`);
    });
  }
  lines.push('');
  const type = order.fulfillment === 'delivery' ? 'Lieferung' : 'Abholung';
  lines.push(`*Lieferart:* ${type}`);
  if (order.fulfillment === 'delivery' && order.customer_address) {
    lines.push(`*Adresse:* ${order.customer_address}`);
  }
  lines.push('');
  lines.push(`*Gesamtsumme:* ${order.total?.toFixed(2)} EUR`);
  if (order.notes) {
    lines.push('');
    lines.push(`*Notiz:* ${order.notes}`);
  }
  lines.push('');
  lines.push('Bitte bestätigen Sie meine Bestellung. Vielen Dank.');
  return lines.join('\n');
}

export function generateNewReservationNotification(reservation) {
  const name = reservation.name || `${reservation.first_name || ''} ${reservation.last_name || ''}`.trim();
  return `🔔 *Neue Reservierung!*\n\n👤 ${name}\n📞 ${reservation.phone}\n📅 ${reservation.date}\n🕐 ${reservation.time}\n👥 ${reservation.party_size} Personen${reservation.notes ? `\n📝 ${reservation.notes}` : ''}`;
}

// Floating WhatsApp button component
export default function WhatsAppFloat() {
  const location = useLocation();
  // Hide on admin pages
  if (location.pathname.startsWith('/admin')) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col items-start gap-1.5">
      <a
        href={getWhatsAppLink('Hallo! Ich möchte gerne bei Little Italy Ristorante bestellen.')}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        className="flex items-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-transform duration-200"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-sm font-semibold hidden sm:inline">Bestellen per WhatsApp</span>
      </a>
      <span className="hidden sm:block ml-2 text-[10px] font-medium text-[hsl(97,18%,34%)] bg-[hsl(97,18%,34%)]/10 px-2.5 py-1 rounded-full border border-[hsl(97,18%,34%)]/20">Bis zu 10 % günstiger als Lieferando</span>
    </div>
  );
}
